/* eslint-disable */
import React, {
  useState,
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
} from 'react';
import { type IComplaintsProps } from 'types/pages';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  // { type SelectChangeEvent }
} from '@mui/material';
import { debounce } from 'lodash';
import Grid from '@mui/material/Grid';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import { createComplaints } from 'service/ComplaintService';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { type RootState } from 'store/rootReducer';
import { uploadChatImage } from 'service/ChatService';
import { createComplatintForSupport } from 'service/OperationalTeamService';
import { CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';

const initialEmployerState: IComplaintsProps = {
  firstName: '',
  lastName: '',
  email: '',
  description: '',
  assigned: [],
};

interface IComplaintsError {
  firstName: string;
  lastName: string;
  email: string;
  description: string;
  complaintTitle: string;
}

interface IAddComplaintProps {
  onClose: () => void;
  getComplaintDetails: () => void;
  supportEmpList: {
    firstName: string;
    lastName: string;
    empId: number;
  }[];
}

const AddComplaints: FC<IAddComplaintProps> = ({
  onClose,
  getComplaintDetails,
  supportEmpList,
}): JSX.Element => {
  const { id } = useParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [complaintTitle, setComplaintTilte] = useState('');
  const [description, setDescription] = useState('');
  const [assign, setAssign] = useState(null);
  const [imageFile, setImageFile] = useState('');
  const [isLoader, setIsLoader] = useState(false);
  const [formError, setFormError] = useState<IComplaintsProps>({
    firstName: '',
    lastName: '',
    email: '',
    description: '',
    complaintTitle: '',
    assigned: [],
  });
  const dispatch = useDispatch();
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current != null) {
      inputRef.current.focus();
    }
  }, []);

  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): undefined | string => {
    switch (type) {
      case 'firstName': {
        setFirstName(e.target.value);
        return;
      }
      case 'lastName': {
        setLastName(e.target.value);
        return;
      }
      case 'email': {
        setEmail(e.target.value);
        return;
      }
      case 'complaintTitle': {
        setComplaintTilte(e.target.value);
        return;
      }
      case 'description': {
        setDescription(e.target.value);
        return;
      }
      case 'assigned': {
        const { value } = e.target;
        setAssign(value.empId);
        return;
      }
      default:
        return '';
    }
  };

  const handleValidation = (
    type: string,
    e: { target: { value: string } },
  ): void => {
    const err: IComplaintsProps = {};
    switch (type) {
      case 'firstName': {
        if (e.target.value === '') {
          err.firstName = strings.displayText.firstNameRequiredText;
        }
        setFormError({ ...formError, firstName: err.firstName });
        break;
      }
      case 'lastName': {
        if (e.target.value === '') {
          err.lastName = strings.displayText.lastNameRequiredText;
        }
        setFormError({ ...formError, lastName: err.lastName });
        break;
      }
      case 'email': {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (e.target.value === '') {
          err.email = strings.displayText.emailEmptyText;
        }
        if (!emailRegex.test(e.target.value)) {
          err.email = strings.displayText.invalidEmailText;
        }
        setFormError({ ...formError, email: err.email });
        break;
      }
      case 'complaintTitle': {
        if (e.target.value === '') {
          err.complaintTitle = strings.displayText.lastNameRequiredText;
        }
        setFormError({ ...formError, complaintTitle: err.complaintTitle });
        break;
      }
      case 'description': {
        if (e.target.value === '') {
          err.description = strings.displayText.lastNameRequiredText;
        }
        setFormError({ ...formError, description: err.description });
        break;
      }
      default:
        break;
    }
  };

  const debounceHandler = useCallback(
    debounce((type: string, e: { target: { value: string } }) => {
      handleValidation(type, e);
    }, 600),
    [],
  );

  const validateForm = (): boolean => {
    const err: IComplaintsError = {
      firstName: '',
      lastName: '',
      email: '',
      description: '',
      complaintTitle: '',
    };
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (email === '') err.email = strings.displayText.emailEmptyText;
    if (!emailRegex.test(email)) {
      err.email = strings.displayText.invalidEmailText;
    }
    if (firstName === '') {
      err.firstName = strings.displayText.firstNameRequiredText;
    }
    if (lastName === '')
      err.lastName = strings.displayText.lastNameRequiredText;

    if (complaintTitle === '')
      err.complaintTitle = strings.displayText.complaintTitleRequiredText;
    if (description === '')
      err.description = strings.displayText.desccriptionRequiredText;

    setFormError({ ...err });
    return Object.keys(err).every((key) => err[key].length === 0);
  };

  const handleFileInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = (e) => {
        // const imageSrc = e.target?.result ;
        void uploadChatImage(file, Number(id)).then((res) => {
          setImageFile(res?.chatImage);
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const formFirstName = data.get('firstName')?.toString() ?? '';
    const formLastName = data.get('lastName')?.toString() ?? '';
    const formComplaintTitle = data.get('complaintTitle')?.toString() ?? '';
    const formDescription = data.get('description')?.toString() ?? '';
    const formEmail = data.get('email')?.toString() ?? '';
    const value = {
      firstName: formFirstName,
      lastName: formLastName,
      email: formEmail,
      senderType: 'employee',
      senderId: empId,
      complaintTitle: formComplaintTitle,
      description: formDescription,
      imageUri: imageFile,
      agentId: assign,
      createdBy: empId,
    };
    const isValid = validateForm();

    if (isValid) {
      setIsLoader(true);
      void (async () => {
        // create complaint service here
        createComplaints(value)
          .then((res) => {
            const { complaintId } = res?.result;
            if (assign !== 0) {
              const data = {
                supportEmployeeId: assign,
                createdBy: empId,
                updatedBy: empId,
                complaintId: complaintId,
              };

              createComplatintForSupport(data);
            }
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.complaintCreatedText,
              }),
            );
            setIsLoader(false);
            getComplaintDetails();
            onClose();
          })
          .catch((err) => {
            setIsLoader(false);
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.msg }),
            );
            console.error(`Something went wrong: ${String(err)}`);
          });
      })();
    }
  };
  const styles = {
    mainBox: {
      maxWidth: '50rem',
    },
    submitBtn: { py: '0.8rem', mt: '1rem' },
  };
  return (
    <Fragment>
      <Box sx={styles.mainBox}>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                inputRef={inputRef}
                margin="normal"
                required
                fullWidth
                label={strings.displayText.firstName}
                name="firstName"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('firstName', e);
                  debounceHandler('firstName', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.firstName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                // inputRef={inputRef}
                margin="normal"
                required
                fullWidth
                label={strings.displayText.lastName}
                name="lastName"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('lastName', e);
                  debounceHandler('lastName', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.lastName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                required
                fullWidth
                label={strings.displayText.emailAddress}
                name="email"
                // onChange={handleInputChange}
                // onBlur={handleValidation}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('email', e);
                  debounceHandler('email', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.email}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                required
                fullWidth
                label={strings.displayText.complaintTitle}
                name="complaintTitle"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('complaintTitle', e);
                  debounceHandler('complaintTitle', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.complaintTitle}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Description"
                name="description"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('description', e);
                  debounceHandler('description', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.description}
              />
            </Grid>

            <Grid item xs={6} my={2}>
              <FormControl fullWidth>
                <InputLabel id="admin-detail-select-label">Assigned</InputLabel>
                <Select
                  labelId="admin-detail-select-label"
                  margin="normal"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleInputChange('assigned', e);
                    debounceHandler('assigned', e);
                  }}
                  label="Assigned"
                >
                  {supportEmpList.map((element, i) => (
                    <MenuItem key={i} value={element}>
                      {`${element.firstName || ''} ${element.lastName || ''}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} my={2}>
              <input
                type="file"
                onChange={handleFileInputChange}
                accept="image/*,application/pdf"
              />
            </Grid>
          </Grid>
          <Buttons
            disabled={isLoader}
            style={styles.submitBtn}
            fullWidth={true}
            type="submit"
            endIcon={isLoader ? <CircularProgress size={15} /> : null}
          >
            {strings.displayText.saveText}
          </Buttons>
        </Box>
      </Box>
    </Fragment>
  );
};

export default AddComplaints;
